import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Loader2, 
  X, 
  Replace, 
  ArrowDown, 
  ChevronDown,
  GripHorizontal,
  Copy,
  Check
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { requestAITextEdit } from '@/lib/aiTextEdit';
import { PartnrMark } from '@/components/partnr/kit/PartnrMark';

interface InlineAIPanelProps {
  selectedText: string;
  fullContent: string;
  onReplace: (newText: string) => void;
  onInsert: (newText: string) => void;
  onClose: () => void;
  position?: { top: number; left: number };
  isPortal?: boolean;
}

// Available AI models via OpenRouter
// Keep in sync with ALLOWED_MODELS in supabase/functions/ai-text-edit/index.ts
const DEFAULT_AI_MODEL = 'google/gemini-3.8-flash';
const AI_MODELS = [
  { id: DEFAULT_AI_MODEL, name: 'Gemini 3.8 Flash', provider: 'Google' },
  { id: 'google/gemini-3-flash-preview', name: 'Gemini 3 Flash', provider: 'Google' },
  { id: 'google/gemini-2.5-flash', name: 'Gemini 2.5 Flash', provider: 'Google' },
  { id: 'google/gemini-2.5-pro', name: 'Gemini 2.5 Pro', provider: 'Google' },
  { id: 'openai/gpt-5-mini', name: 'GPT-5 Mini', provider: 'OpenAI' },
  { id: 'openai/gpt-5', name: 'GPT-5', provider: 'OpenAI' },
];

const QUICK_ACTIONS = [
  { label: 'Improve writing', prompt: 'Improve this text while keeping the same meaning' },
  { label: 'Simplify', prompt: 'Simplify this text for easier reading' },
];

export function InlineAIPanel({
  selectedText,
  fullContent,
  onReplace,
  onInsert,
  onClose,
  position,
  isPortal = false,
}: InlineAIPanelProps) {
  const [prompt, setPrompt] = useState('');
  const [response, setResponse] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState(DEFAULT_AI_MODEL);
  const [copied, setCopied] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);

  const currentModel = AI_MODELS.find(m => m.id === selectedModel) || AI_MODELS[0];
  const textToProcess = selectedText || fullContent;
  const hasSelection = selectedText.length > 0;

  // Position constraints
  const getConstrainedPosition = useCallback(() => {
    if (!panelRef.current || !position) return { top: 0, left: 0 };
    
    const rect = panelRef.current.getBoundingClientRect();
    const padding = 10;
    
    let newTop = position.top + dragOffset.y;
    let newLeft = position.left + dragOffset.x;

    if (newLeft < padding) newLeft = padding;
    if (newLeft + rect.width > window.innerWidth - padding) {
      newLeft = window.innerWidth - rect.width - padding;
    }
    if (newTop < padding) newTop = padding;
    if (newTop + rect.height > window.innerHeight - padding) {
      newTop = window.innerHeight - rect.height - padding;
    }

    return { top: newTop, left: newLeft };
  }, [position, dragOffset]);

  // Initial position adjustment
  useEffect(() => {
    if (panelRef.current && position) {
      const rect = panelRef.current.getBoundingClientRect();
      const padding = 10;
      let adjustX = 0;
      let adjustY = 0;

      if (rect.right > window.innerWidth - padding) {
        adjustX = window.innerWidth - padding - rect.right;
      }
      if (rect.left < padding) {
        adjustX = padding - rect.left;
      }
      if (rect.bottom > window.innerHeight - padding) {
        adjustY = window.innerHeight - padding - rect.bottom;
      }

      if (adjustX !== 0 || adjustY !== 0) {
        setDragOffset({ x: adjustX, y: adjustY });
      }
    }
  }, [position]);

  // Drag handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX - dragOffset.x, y: e.clientY - dragOffset.y };
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !dragStartRef.current) return;
      e.preventDefault();
      setDragOffset({
        x: e.clientX - dragStartRef.current.x,
        y: e.clientY - dragStartRef.current.y,
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      dragStartRef.current = null;
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const sendToAI = async (userPrompt: string) => {
    setIsLoading(true);
    setError(null);
    setResponse('');

    try {
      const result = await requestAITextEdit({
        selectedText: textToProcess,
        instruction: userPrompt,
        model: selectedModel,
      });
      setResponse(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get AI response');
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAction = (actionPrompt: string) => {
    sendToAI(actionPrompt);
  };

  const handleCustomPrompt = () => {
    if (!prompt.trim()) return;
    sendToAI(prompt.trim());
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCustomPrompt();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(response);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClear = () => {
    setResponse('');
    setPrompt('');
    setError(null);
  };

  const constrainedPos = position ? getConstrainedPosition() : null;

  return (
    <div
      ref={panelRef}
      className={cn(
        "bg-popover border border-border rounded-xl shadow-2xl overflow-hidden",
        isPortal ? "fixed z-[9999]" : "absolute bottom-14 right-3 z-50",
        "w-80 max-h-[80vh] flex flex-col",
        isDragging && "cursor-grabbing"
      )}
      style={isPortal && position ? {
        top: position.top + dragOffset.y,
        left: position.left + dragOffset.x,
      } : undefined}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div 
        className="bg-partnr text-partnr-foreground px-4 py-3 flex items-center justify-between cursor-grab active:cursor-grabbing"
        onMouseDown={position ? handleMouseDown : undefined}
      >
        <div className="flex items-center gap-2">
          {position && <GripHorizontal className="h-4 w-4 opacity-60" />}
          <PartnrMark size={16} />
          <span className="font-medium text-sm">Partnr</span>
        </div>

        {/* Model Selector */}
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1 px-2 py-1 bg-white/20 hover:bg-white/30 rounded text-xs transition-colors">
                <span>{currentModel.name}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {AI_MODELS.map(model => (
                <DropdownMenuItem
                  key={model.id}
                  onClick={() => setSelectedModel(model.id)}
                  className={cn(
                    "flex items-center justify-between",
                    selectedModel === model.id && "bg-partnr/10 text-partnr"
                  )}
                >
                  <span>{model.name}</span>
                  <span className="text-xs text-muted-foreground">{model.provider}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            onClick={onClose}
            className="hover:bg-white/20 rounded p-1 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Selected text indicator */}
      {hasSelection && (
        <div className="px-4 py-2 bg-partnr/10 border-b border-partnr/20">
          <p className="text-xs text-partnr font-medium mb-1">Selected text:</p>
          <p className="text-sm text-foreground line-clamp-2">
            "{selectedText.substring(0, 100)}{selectedText.length > 100 ? '...' : ''}"
          </p>
        </div>
      )}

      {/* Quick Actions */}
      {!response && !isLoading && (
        <div className="p-3 border-b border-border">
          <p className="text-xs text-muted-foreground mb-2">Quick actions:</p>
          <div className="flex flex-wrap gap-1">
            {QUICK_ACTIONS.map(action => (
              <button
                key={action.label}
                onClick={() => handleQuickAction(action.prompt)}
                className="px-2 py-1 text-xs bg-muted hover:bg-partnr/20 hover:text-partnr rounded-full transition-colors"
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="p-4 flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin text-partnr" />
          <span className="text-sm">Thinking...</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="px-4 py-2">
          <div className="text-xs text-destructive bg-destructive/10 p-2 rounded">
            {error}
          </div>
        </div>
      )}

      {/* Response Area */}
      {response && !isLoading && (
        <div className="p-4 border-b border-border">
          <p className="text-sm text-foreground whitespace-pre-wrap max-h-48 overflow-y-auto">
            {response}
          </p>
          
          <div className="flex gap-2 mt-3">
            {hasSelection ? (
              <Button
                size="sm"
                onClick={() => {
                  onReplace(response);
                  onClose();
                }}
                className="flex-1 bg-partnr hover:bg-partnr/90"
              >
                <Replace className="h-3.5 w-3.5 mr-1" />
                Replace
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => {
                  onInsert(response);
                  onClose();
                }}
                className="flex-1 bg-partnr hover:bg-partnr/90"
              >
                <ArrowDown className="h-3.5 w-3.5 mr-1" />
                Insert
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={handleCopy}
              className="px-3"
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleClear}
              className="text-muted-foreground"
            >
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Custom Prompt Input */}
      <div className="p-3">
        <div className="flex gap-2">
          <Input
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything..."
            className="flex-1 text-sm focus-visible:ring-partnr"
            disabled={isLoading}
          />
          <Button
            onClick={handleCustomPrompt}
            disabled={isLoading || !prompt.trim()}
            size="sm"
            className="bg-partnr hover:bg-partnr/90"
          >
            Send
          </Button>
        </div>
        
        {/* Model indicator */}
        <p className="text-xs text-muted-foreground mt-2 text-center">
          Using {currentModel.name} by {currentModel.provider}
        </p>
      </div>
    </div>
  );
}
